import type { ReactElement, ReactNode } from "react"

import { QueryClient, type QueryKey } from "@tanstack/react-query"
import type { NavigateFunction } from "react-router-dom"

import type { TTFunction } from "$/i18n/i18n"
import type { TranslationObj } from "$/utils/lang"

import type { TAccordionRenderFunction } from "@/3514/components/shared/accordion/accordion.types"
import type { TButtonProps } from "@/3514/components/shared/button/button.component"
import type { TSessionTimeState } from "@/3514/hooks"
import type { TModalDispatcher } from "@/3514/store/modal"
import {
    ESessionAttendanceStatus,
    ESessionExtraPartyRole,
    ESessionInvitationStatus,
    ESessionModality,
    EUserRole
} from "@/3514/types"
import type { TEmptyAsyncCallback, TEmptyCallback, TEmptyRenderCallback } from "@/shared/types/functions"

type TSessionInfoParticipant = {
    id: number
    firstName: string
    lastName: string
    photo?: string | null
    rsvpStatus: ESessionInvitationStatus
    attendanceStatus: ESessionAttendanceStatus
    extraPartyRole: string | null
    isExtraParty: boolean
    isGuest: boolean
    isHost: boolean
}

type TSessionInfoHeaderSessionData = {
    moduleId: number | null
    moduleTitle: TranslationObj | null
    sessionId: number
    isModuleReflectionPassed: boolean
    isSessionAttendanceMarked: boolean
    isSessionCancelled: boolean
}

type TSessionInfoData = {
    id: number
    participants: TSessionInfoParticipant[]
    minHoursToEdit: number
    groupId: number | null
    joinUrl: string | undefined
    duration: number
    startDate: string
    name: string
    modality: ESessionModality
    rescheduleCount: number
    isMissed: boolean
    isGifted: boolean
    isSwappable: boolean
    isReschedulable: boolean
    isAttendanceMarked: boolean
    isCancelled: boolean
    isExtraParty: boolean
}

enum ESessionInfoColorSchema {
    BlueBorderWhiteBg = "blueBorderWhiteBg",
    TransparentBorderBlueBg = "transparentBorderBlueBg",
    AccentBorderWhiteBg = "accentBorderWhiteBg",
    OrangeBorderWhiteBg = "orangeBorderWhiteBg"
}

type TSessionInfoRenderHeaderIconFunction = TAccordionRenderFunction

type _TSessionInfoUserData = { id: number | -1; firstName: string; lastName: string; photo: string | null }

type _TSessionInfoCoachData = { id: number | null; firstName: string | null; photo: string | null }

type TSessionInfoExtraPartyData = {
    id: number | null
    email: string | null
    firstName: string | null
    lastName: string | null
    label: string | null
    role: ESessionExtraPartyRole
    invitationStatus: ESessionInvitationStatus
    attendanceStatus: ESessionAttendanceStatus
}

type _TSessionInfoMessageComponentProps =
    | {
          text: string
          typeIcon: ReactElement
          helpIcon: ReactElement | null
          isAccessible: false
          ariaLabel?: never
          action?: never
      }
    | {
          text: string
          typeIcon: ReactElement
          helpIcon: ReactElement
          isAccessible: boolean
          ariaLabel: string
          action: TEmptyCallback
      }

type _TSessionInfoRenderFunctionSharedInternalParams = {
    data: {
        session: TSessionInfoData
        user: _TSessionInfoUserData
        coach: _TSessionInfoCoachData
        extraParties: TSessionInfoExtraPartyData[] | undefined
    }
    viewerRole: EUserRole
    sessionsQueryKey: QueryKey
    sessionTimeState: TSessionTimeState
}

type _TSessionInfoRenderFunctionSharedExternalParams = {
    t: TTFunction
    isNetworkFast: boolean
    qc: QueryClient
    modalDispatcher: TModalDispatcher
    navigate: NavigateFunction
}

type _TSessionInfoRenderMessagesPropsFunctionInternalParams = {
    session: TSessionInfoData
    sessionsQueryKey: QueryKey
    isAccordionExpandable: boolean
    isAccordionExpanded: boolean
}

type TSessionInfoRenderMessagesPropsFunction = (
    params: _TSessionInfoRenderMessagesPropsFunctionInternalParams &
        Partial<_TSessionInfoRenderFunctionSharedExternalParams>
) => _TSessionInfoMessageComponentProps[]

type _TSessionInfoRenderButtonPropsFunctionInternalParams = _TSessionInfoRenderFunctionSharedInternalParams & {
    isAccordionExpandable: boolean
    isAccordionExpanded: boolean
}

type TSessionInfoRenderButtonPropsFunction = (
    params: _TSessionInfoRenderButtonPropsFunctionInternalParams &
        Partial<_TSessionInfoRenderFunctionSharedExternalParams>
) => TButtonProps[]

type TSessionInfoRenderNoticeFunction = (
    params: Pick<TSessionTimeState, "isLive" | "isUpcoming"> & {
        modality: ESessionModality
        isMissed: boolean
        minDaysToEdit: number
    }
) => ReactNode

type TSelectInfoDropdownItem = {
    label: string
    ariaLabel: string
    focusHandler: TEmptyCallback | TEmptyAsyncCallback
    actionHandler: TEmptyCallback | TEmptyAsyncCallback
}

type TSessionInfoRenderActionsPropsFunction = (
    params: _TSessionInfoRenderFunctionSharedInternalParams & Partial<_TSessionInfoRenderFunctionSharedExternalParams>
) => TSelectInfoDropdownItem[]

type _TSessionInfoRenderAttendanceListItem = (params: {
    viewerRole: EUserRole
    participant: TSessionInfoParticipant
    groupId: number
    modality: ESessionModality
}) => TAccordionRenderFunction

type TSessionInfoProps = {
    tag: "section" | "div"
    viewerRole: EUserRole
    isExpandable: boolean
    isDefaultExpanded: boolean
    withLiveChecker: boolean
    onLiveSessionStarted: TEmptyAsyncCallback
    onLiveSessionPassed: TEmptyAsyncCallback
    getColorSchema(params: { isLive: boolean; isMissed: boolean; isCancelled: boolean }): ESessionInfoColorSchema
    sessionsQueryKey: QueryKey | null
    onAttendeesListHover(participantId: number | -1, sessionModality: ESessionModality): Promise<void>
    data: {
        session: TSessionInfoData
        user: _TSessionInfoUserData
        coach: _TSessionInfoCoachData
        extraParties: TSessionInfoExtraPartyData[] | undefined
    }
    renderers: {
        renderNotice: TSessionInfoRenderNoticeFunction
        renderButtonsProps: TSessionInfoRenderButtonPropsFunction
        renderMessagesProps: TSessionInfoRenderMessagesPropsFunction
        renderAttendanceListItem: _TSessionInfoRenderAttendanceListItem
        renderHeaderDropdown(params: _TSessionInfoRenderFunctionSharedInternalParams): ReactElement
        renderHeaderAvatar: TEmptyRenderCallback
        renderHeaderChevron: TAccordionRenderFunction
        renderHeaderNameToolip: TEmptyRenderCallback
        renderPostSessionModalOpener: TEmptyRenderCallback | undefined
        renderCancelledSessionModalOpener: TEmptyRenderCallback
        renderAttendanceModalOpener: TEmptyRenderCallback | undefined
    }
}

type TSessionInfoHeaderProps = {
    renderHeaderAvatar: TEmptyRenderCallback
    renderHeaderNameToolip: TEmptyRenderCallback
} & {
    data: {
        session: Pick<TSessionInfoData, "startDate" | "name" | "duration" | "id" | "isCancelled">
        coach: _TSessionInfoCoachData
        user: _TSessionInfoUserData | undefined
        extraParties: TSessionInfoExtraPartyData[] | undefined
    }
    isAccordionExpanded?: boolean
    className?: string
}

type _TSessionInfoBodyProps = Pick<
    TSessionInfoProps,
    "data" | "viewerRole" | "sessionsQueryKey" | "onAttendeesListHover"
> & {
    colorSchema: ESessionInfoColorSchema
    renderAttendanceListItem: _TSessionInfoRenderAttendanceListItem
    renderButtonsProps: TSessionInfoRenderButtonPropsFunction
    renderMessagesProps: TSessionInfoRenderMessagesPropsFunction
    renderNotice: TSessionInfoRenderNoticeFunction
    sessionTimeState: TSessionTimeState
    isAccordionExpandable?: boolean
    isAccordionExpanded?: boolean
}

export type {
    TSessionInfoParticipant,
    TSessionInfoRenderHeaderIconFunction,
    TSessionInfoExtraPartyData,
    _TSessionInfoMessageComponentProps,
    _TSessionInfoRenderMessagesPropsFunctionInternalParams,
    TSessionInfoRenderMessagesPropsFunction,
    _TSessionInfoRenderButtonPropsFunctionInternalParams,
    TSessionInfoRenderButtonPropsFunction,
    TSessionInfoData,
    TSessionInfoHeaderSessionData,
    TSessionInfoRenderNoticeFunction,
    TSessionInfoProps,
    _TSessionInfoUserData,
    _TSessionInfoCoachData,
    TSessionInfoHeaderProps,
    _TSessionInfoBodyProps,
    _TSessionInfoRenderAttendanceListItem,
    TSelectInfoDropdownItem,
    TSessionInfoRenderActionsPropsFunction,
    _TSessionInfoRenderFunctionSharedInternalParams,
    _TSessionInfoRenderFunctionSharedExternalParams
}
export { ESessionInfoColorSchema }
